import type {FieldNamesMarkedBoolean} from "react-hook-form";

import type {CreateVehicleRequest, UpdateVehicleRequest, VehicleFormData} from "../types";

type VehicleWriteBase = Omit<
  CreateVehicleRequest,
  "make" | "model" | "vehicle_make_id" | "vehicle_model_id" | "plate" | "vin"
>;

function normalizeIdentityFields(data: VehicleFormData) {
  const plateTrimmed = data.plate?.trim();
  const vinTrimmed = data.vin?.trim();
  const secondaryPlateTrimmed = data.secondary_plate?.trim();

  return {
    plate: plateTrimmed ? plateTrimmed : null,
    vin: vinTrimmed ? vinTrimmed : null,
    secondary_plate: secondaryPlateTrimmed ? secondaryPlateTrimmed : null,
  };
}

export function buildVehicleWritePayload(
  data: VehicleFormData,
  base: VehicleWriteBase,
): CreateVehicleRequest {
  const make = data.make.trim();
  const model = data.model.trim();
  const hasMakeId =
    data.vehicle_make_id != null && Number.isFinite(data.vehicle_make_id);
  const hasModelId =
    data.vehicle_model_id != null && Number.isFinite(data.vehicle_model_id);

  const {plate, vin, secondary_plate} = normalizeIdentityFields(data);

  const payload: CreateVehicleRequest = {
    ...base,
    plate,
    make,
    model,
    secondary_plate,
    vin,
    has_dealers_plate: data.has_dealers_plate ?? false,
  };

  if (hasMakeId && hasModelId) {
    payload.vehicle_make_id = data.vehicle_make_id!;
    payload.vehicle_model_id = data.vehicle_model_id!;
  } else if (hasMakeId) {
    payload.vehicle_make_id = data.vehicle_make_id!;
  }

  return payload;
}

export function buildVehicleUpdatePayload(
  data: VehicleFormData,
  base: VehicleWriteBase,
  dirtyFields: FieldNamesMarkedBoolean<VehicleFormData>,
): UpdateVehicleRequest {
  const payload = buildVehicleWritePayload(data, base);
  const plateDirty = Boolean(dirtyFields.plate);
  const vinDirty = Boolean(dirtyFields.vin);

  if (!plateDirty && !vinDirty) {
    const {plate: _plate, vin: _vin, ...rest} = payload;
    return rest;
  }

  const updatePayload: UpdateVehicleRequest = {...payload};
  if (!plateDirty) {
    delete updatePayload.plate;
  }
  if (!vinDirty) {
    delete updatePayload.vin;
  }

  return updatePayload;
}
